/**
 * pilotFeedbackVocabulary — the one operator-facing vocabulary for the pilot feedback surface
 * (FULL-0164 / PROD-UX-078).
 *
 * `backend/app/services/pilot_feedback.py` is the authority: it owns the closed sets, validates
 * every capture and publishes the same sets in the API `contract` block. These labels are
 * presentation only and never turn a value into a permission, a priority or a triage decision.
 *
 * `uxSignal` is the six-question set MASTER_FLOW §14C.6 requires pilot feedback to capture,
 * plus "other". `highImpact` is exactly severity blocker/high — the same rule the backend
 * computes — and is used only to mark an item for explicit triage, never to classify it.
 */

export const SEVERITIES = Object.freeze(["blocker", "high", "medium", "low"]);
export const HIGH_IMPACT_SEVERITIES = Object.freeze(["blocker", "high"]);

export const WORKFLOWS = Object.freeze([
  "ticket_queue",
  "digital_employee_workspace",
  "approval",
  "manager_console",
  "employee_builder",
  "remote_operations",
  "system_map",
  "specialist_workspace",
  "helpdesk_home",
  "other",
]);

export const REPRODUCIBILITIES = Object.freeze(["always", "often", "intermittent", "once", "unknown"]);

export const UX_SIGNALS = Object.freeze([
  "system_state",
  "ownership",
  "approval_clarity",
  "recommendation_vs_verified",
  "uncertainty_or_wait",
  "technical_or_slow",
  "other",
]);

export const TRIAGE_OUTCOMES = Object.freeze(["dev_fix", "pilot_blocker"]);

const SEVERITY_META = Object.freeze({
  blocker: { label: "חוסם", className: "bg-red-100 text-red-800 border-red-200" },
  high: { label: "גבוה", className: "bg-orange-100 text-orange-800 border-orange-200" },
  medium: { label: "בינוני", className: "bg-amber-100 text-amber-800 border-amber-200" },
  low: { label: "נמוך", className: "bg-gray-100 text-gray-700 border-gray-200" },
});

const WORKFLOW_LABELS = Object.freeze({
  ticket_queue: "תור קריאות",
  digital_employee_workspace: "סביבת עובד דיגיטלי",
  approval: "אישור פעולה",
  manager_console: "קונסולת מנהל",
  employee_builder: "בונה עובדים",
  remote_operations: "פעולות מרחוק",
  system_map: "מפת מערכת",
  specialist_workspace: "סביבת מומחה",
  helpdesk_home: "בית העובדים",
  other: "אחר",
});

const REPRODUCIBILITY_LABELS = Object.freeze({
  always: "בכל פעם",
  often: "לרוב",
  intermittent: "לסירוגין",
  once: "פעם אחת",
  unknown: "לא ידוע",
});

const UX_SIGNAL_LABELS = Object.freeze({
  system_state: "לא היה ברור מה המערכת עושה",
  ownership: "לא היה ברור מי הבעלים של העבודה",
  approval_clarity: "לא היה ברור מה דורש אישור ולמה",
  recommendation_vs_verified: "לא היה ברור אם זו המלצה או ראיה מאומתת",
  uncertainty_or_wait: "אי-ודאות, המתנה או צורך בלוגים/תמיכה",
  technical_or_slow: "מסך או פעולה טכניים, מעורפלים או איטיים",
  other: "אחר",
});

const OUTCOME_META = Object.freeze({
  dev_fix: { label: "תוקן בפיתוח", className: "bg-green-100 text-green-800 border-green-200" },
  pilot_blocker: { label: "חוסם ידני/פיילוט", className: "bg-red-100 text-red-800 border-red-200" },
  unclassified: { label: "לא סווג", className: "bg-gray-100 text-gray-700 border-gray-200" },
});

const UNKNOWN = "לא ידוע";

export function severityMeta(value) {
  return SEVERITY_META[value] || { label: UNKNOWN, className: "bg-gray-100 text-gray-700 border-gray-200" };
}

export function workflowLabel(value) {
  return WORKFLOW_LABELS[value] || UNKNOWN;
}

export function reproducibilityLabel(value) {
  return REPRODUCIBILITY_LABELS[value] || UNKNOWN;
}

export function uxSignalLabel(value) {
  return UX_SIGNAL_LABELS[value] || UNKNOWN;
}

export function outcomeMeta(value) {
  return OUTCOME_META[value] || OUTCOME_META.unclassified;
}

export function isHighImpact(severity) {
  return HIGH_IMPACT_SEVERITIES.includes(severity);
}

/**
 * Client-side early refusal mirroring the high-confidence patterns the server's shared
 * credential detector refuses. The server remains authoritative; this only avoids a round trip
 * for the obvious cases and never echoes the matched text.
 */
const CLIENT_SECRET_PATTERNS = Object.freeze([
  /-----BEGIN [A-Z ]*PRIVATE KEY-----/,
  /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/,
  /(?:password|passwd|pwd|api[_-]?key|secret|token)\s*[:=]\s*\S{4,}/i,
  /\bbearer\s+[A-Za-z0-9._~+/=-]{16,}/i,
  /\bAKIA[0-9A-Z]{16}\b/,
  /\b(?:sk|pk|rk)-[A-Za-z0-9_-]{16,}/,
]);

export const SECRET_REFUSAL =
  "הטקסט נראה כמו סיסמה, מפתח או טוקן ולכן לא נשלח. הסירו את הסוד ונסו שוב.";

export function looksLikeSecret(text) {
  if (typeof text !== "string" || !text) return false;
  return CLIENT_SECRET_PATTERNS.some((pattern) => pattern.test(text));
}
